"use client";

import { useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Info,
  Link2,
  Network,
  Play,
  ShieldCheck,
  Sparkles,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ClearButton } from "@/components/tools/clear-button";
import { CopyButton } from "@/components/tools/copy-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import {
  formatRedirectChainReport,
  isRedirectStatus,
  redirectStatusType,
  statusTone,
  type RedirectChainAnalysis,
  type RedirectChainFindingStatus,
  type RedirectHop,
} from "@/lib/tools/redirect-chain";
import {
  formatSecurityHeaderAuditReport,
  type SecurityHeaderAudit,
  type SecurityHeaderFinding,
  type SecurityHeaderStatus,
} from "@/lib/tools/security-headers";
import { cn } from "@/lib/utils";

type HeaderCheckResponse = {
  requestedUrl: string;
  finalUrl: string;
  method: "HEAD" | "GET";
  status: number;
  statusText: string;
  ok: boolean;
  redirected: boolean;
  durationMs: number;
  headers: Array<{
    name: string;
    value: string;
  }>;
};

type RedirectChainResponse = {
  requestedUrl: string;
  finalUrl: string;
  method: "HEAD" | "GET";
  methodFallback: boolean;
  maxRedirects: number;
  terminalReason:
    | "complete"
    | "redirect-loop"
    | "too-many-redirects"
    | "blocked-redirect"
    | "redirect-without-location";
  totalDurationMs: number;
  redirectCount: number;
  warning?: string;
  hops: RedirectHop[];
  analysis: RedirectChainAnalysis;
};

type SecurityRedirectHop = {
  url: string;
  status: number;
  statusText: string;
  location?: string;
  durationMs: number;
};

type SecurityHeadersAuditResponse = {
  requestedUrl: string;
  finalUrl: string;
  method: "HEAD" | "GET";
  status: number;
  statusText: string;
  ok: boolean;
  redirected: boolean;
  durationMs: number;
  redirectChain: SecurityRedirectHop[];
  headers: Array<{
    name: string;
    value: string;
  }>;
  audit: SecurityHeaderAudit;
};

function stringifyHeaders(result: HeaderCheckResponse | null) {
  if (!result) {
    return "";
  }

  return [
    `${result.method} ${result.finalUrl}`,
    `${result.status} ${result.statusText}`,
    "",
    ...result.headers.map((header) => `${header.name}: ${header.value}`),
  ].join("\n");
}

function HeaderResult({ result }: { result: HeaderCheckResponse | null }) {
  if (!result) {
    return <CodeOutput placeholder="Response headers will appear here." />;
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-md border border-zinc-200">
        <table className="w-full text-left text-sm">
          <tbody>
            <tr className="border-b border-zinc-100">
              <th className="w-36 bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Status</th>
              <td className="px-3 py-2 font-mono text-zinc-950">
                {result.status} {result.statusText || (result.ok ? "OK" : "")}
              </td>
            </tr>
            <tr className="border-b border-zinc-100">
              <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Method</th>
              <td className="px-3 py-2 font-mono text-zinc-950">{result.method}</td>
            </tr>
            <tr className="border-b border-zinc-100">
              <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Final URL</th>
              <td className="break-all px-3 py-2 font-mono text-zinc-950">{result.finalUrl}</td>
            </tr>
            <tr>
              <th className="bg-zinc-50 px-3 py-2 font-medium text-zinc-600">Time</th>
              <td className="px-3 py-2 font-mono text-zinc-950">{result.durationMs} ms</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="overflow-hidden rounded-md border border-zinc-200">
        <table className="w-full text-left text-sm">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>
              <th className="w-48 px-3 py-2 font-medium">Header</th>
              <th className="px-3 py-2 font-medium">Value</th>
            </tr>
          </thead>
          <tbody>
            {result.headers.map((header) => (
              <tr key={header.name} className="border-t border-zinc-100">
                <td className="break-all px-3 py-2 font-mono text-zinc-700">{header.name}</td>
                <td className="break-all px-3 py-2 font-mono text-zinc-950">{header.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function HeadersCheckerTool() {
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState<"HEAD" | "GET">("HEAD");
  const [result, setResult] = useState<HeaderCheckResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const copyValue = useMemo(() => stringifyHeaders(result), [result]);

  const clear = () => {
    setUrl("");
    setMethod("HEAD");
    setResult(null);
    setError("");
  };

  const checkHeaders = async () => {
    if (!url.trim()) {
      setError("Enter a URL first.");
      return;
    }

    setBusy(true);
    setError("");
    setResult(null);

    try {
      const response = await fetch("/api/headers-checker", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url, method }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error ?? "Headers could not be checked.");
      }

      setResult(data as HeaderCheckResponse);
    } catch (checkError) {
      setError(checkError instanceof Error ? checkError.message : "Headers could not be checked.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="Header request"
      outputTitle="Headers"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setUrl("https://example.com")}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton onClear={clear} />
        </>
      }
      outputActions={<CopyButton value={copyValue} label="Copy headers" />}
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="headers-url">URL</Label>
            <Input
              id="headers-url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://example.com"
            />
          </div>
          <div className="max-w-xs space-y-2">
            <Label htmlFor="headers-method">Method</Label>
            <Select
              id="headers-method"
              value={method}
              onChange={(event) => setMethod(event.target.value as "HEAD" | "GET")}
            >
              <option value="HEAD">HEAD</option>
              <option value="GET">GET</option>
            </Select>
          </div>
          <ErrorMessage message={error} />
          <Button type="button" onClick={checkHeaders} disabled={busy}>
            {busy ? <Network className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {busy ? "Checking..." : "Check headers"}
          </Button>
        </div>
      }
      output={<HeaderResult result={result} />}
    />
  );
}

const statusToneClasses: Record<ReturnType<typeof statusTone>, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  redirect: "border-sky-200 bg-sky-50 text-sky-800",
  "client-error": "border-amber-200 bg-amber-50 text-amber-800",
  "server-error": "border-red-200 bg-red-50 text-red-800",
  other: "border-zinc-200 bg-zinc-50 text-zinc-700",
};

const findingStatusClasses: Record<RedirectChainFindingStatus, string> = {
  pass: "border-emerald-200 bg-emerald-50 text-emerald-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  fail: "border-red-200 bg-red-50 text-red-800",
  info: "border-sky-200 bg-sky-50 text-sky-800",
};

function stringifyRedirectHeaders(hop: RedirectHop) {
  return hop.headers.map((header) => `${header.name}: ${header.value}`).join("\n");
}

function stringifyRedirectResult(result: RedirectChainResponse | null) {
  if (!result) {
    return "";
  }

  return formatRedirectChainReport({
    requestedUrl: result.requestedUrl,
    method: result.method,
    analysis: result.analysis,
    hops: result.hops,
  });
}

function scoreTone(score: number) {
  if (score >= 88) {
    return "text-emerald-700";
  }

  if (score >= 65) {
    return "text-amber-700";
  }

  return "text-red-700";
}

function statusLabel(hop: RedirectHop) {
  const type = redirectStatusType(hop.status);

  if (type === "permanent") {
    return "Permanent";
  }

  if (type === "temporary") {
    return "Temporary";
  }

  if (isRedirectStatus(hop.status)) {
    return "Redirect";
  }

  if (hop.status >= 200 && hop.status < 300) {
    return "Final";
  }

  return "Response";
}

function RedirectHopCard({ hop, isLast }: { hop: RedirectHop; isLast: boolean }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              Hop {hop.index}
            </span>
            <span
              className={cn(
                "rounded-md border px-2 py-1 text-xs font-medium",
                statusToneClasses[statusTone(hop.status)],
              )}
            >
              {hop.status} {hop.statusText || statusLabel(hop)}
            </span>
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              {statusLabel(hop)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              <Clock3 className="h-3.5 w-3.5" />
              {hop.durationMs} ms
            </span>
          </div>
          <p className="break-all font-mono text-sm text-zinc-950">{hop.url}</p>
        </div>
        {isLast ? (
          <span className="shrink-0 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800">
            Final URL
          </span>
        ) : null}
      </div>
      {hop.resolvedLocation ? (
        <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
          <p className="text-xs font-medium uppercase text-zinc-500">Location</p>
          <p className="mt-1 break-all font-mono text-sm text-zinc-900">{hop.resolvedLocation}</p>
        </div>
      ) : null}
    </div>
  );
}

function RedirectFindings({ analysis }: { analysis: RedirectChainAnalysis }) {
  return (
    <div className="space-y-3">
      {analysis.findings.map((finding) => (
        <div key={finding.id} className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "rounded-md border px-2 py-1 text-xs font-medium",
                findingStatusClasses[finding.status],
              )}
            >
              {finding.status}
            </span>
            {finding.evidence ? (
              <span className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
                evidence
              </span>
            ) : null}
          </div>
          <h3 className="text-sm font-semibold text-zinc-950">{finding.title}</h3>
          <p className="mt-1 text-sm leading-6 text-zinc-600">{finding.summary}</p>
          <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm leading-6 text-zinc-700">
            {finding.recommendation}
          </div>
          {finding.evidence ? (
            <CodeOutput value={finding.evidence} className="mt-3 min-h-0 text-xs" />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function RedirectChainResult({
  result,
  view,
}: {
  result: RedirectChainResponse | null;
  view: "timeline" | "insights" | "headers";
}) {
  if (!result) {
    return <CodeOutput placeholder="Redirect hops, timing, and SEO insights will appear here." />;
  }

  if (view === "headers") {
    return (
      <div className="space-y-4">
        {result.hops.map((hop) => (
          <div key={`${hop.index}-${hop.url}`} className="space-y-2">
            <h3 className="text-sm font-semibold text-zinc-700">
              Hop {hop.index} headers - {hop.status}
            </h3>
            <CodeOutput
              value={stringifyRedirectHeaders(hop)}
              placeholder="No headers were returned for this hop."
              className="min-h-0 text-xs"
            />
          </div>
        ))}
      </div>
    );
  }

  if (view === "insights") {
    return <RedirectFindings analysis={result.analysis} />;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-[10rem_1fr]">
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-center">
          <p className={cn("text-5xl font-semibold", scoreTone(result.analysis.score))}>
            {result.analysis.grade}
          </p>
          <p className="mt-2 font-mono text-sm text-zinc-600">{result.analysis.score}/100</p>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <p className="text-zinc-500">Final URL</p>
              <p className="mt-1 break-all font-mono text-zinc-950">{result.analysis.finalUrl}</p>
            </div>
            <div>
              <p className="text-zinc-500">Final status</p>
              <p className="mt-1 font-mono text-zinc-950">{result.analysis.finalStatus}</p>
            </div>
            <div>
              <p className="text-zinc-500">Redirects</p>
              <p className="mt-1 font-mono text-zinc-950">{result.analysis.redirectCount}</p>
            </div>
            <div>
              <p className="text-zinc-500">Total time</p>
              <p className="mt-1 font-mono text-zinc-950">
                {result.analysis.totalDurationMs} ms
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm leading-6 text-zinc-600">{result.analysis.summary}</p>
          {result.methodFallback ? (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              HEAD was not supported, so the checker retried with a byte-range GET request.
            </p>
          ) : null}
          {result.warning ? (
            <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {result.warning}
            </p>
          ) : null}
        </div>
      </div>

      {result.analysis.topFixes.length ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-900">Priority cleanup</h3>
          <div className="mt-2 space-y-2 text-sm leading-6 text-amber-800">
            {result.analysis.topFixes.map((finding) => (
              <p key={finding.id}>
                <span className="font-medium">{finding.title}:</span> {finding.recommendation}
              </p>
            ))}
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        {result.hops.map((hop, index) => (
          <RedirectHopCard
            key={`${hop.index}-${hop.url}`}
            hop={hop}
            isLast={index === result.hops.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export function RedirectChainCheckerTool() {
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState<"HEAD" | "GET">("HEAD");
  const [maxRedirects, setMaxRedirects] = useState(10);
  const [result, setResult] = useState<RedirectChainResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<"timeline" | "insights" | "headers">("timeline");
  const copyValue = useMemo(() => stringifyRedirectResult(result), [result]);

  const clear = () => {
    setUrl("");
    setMethod("HEAD");
    setMaxRedirects(10);
    setResult(null);
    setError("");
    setView("timeline");
  };

  const checkRedirects = async () => {
    if (!url.trim()) {
      setError("Enter a URL first.");
      return;
    }

    setBusy(true);
    setError("");
    setResult(null);

    try {
      const response = await fetch("/api/redirect-chain-checker", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url, method, maxRedirects }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error ?? "Redirect chain could not be checked.");
      }

      setResult(data as RedirectChainResponse);
      setView("timeline");
    } catch (checkError) {
      setError(
        checkError instanceof Error ? checkError.message : "Redirect chain could not be checked.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="Redirect request"
      outputTitle="Redirect chain"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setUrl("http://github.com")}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton onClear={clear} />
        </>
      }
      outputActions={
        <>
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {(["timeline", "insights", "headers"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={view === option ? "default" : "ghost"}
                onClick={() => setView(option)}
              >
                {option === "timeline" ? "Timeline" : option === "insights" ? "Insights" : "Headers"}
              </Button>
            ))}
          </div>
          <CopyButton value={copyValue} label="Copy report" />
        </>
      }
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="redirect-url">URL</Label>
            <Input
              id="redirect-url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://example.com"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="redirect-method">Method</Label>
              <Select
                id="redirect-method"
                value={method}
                onChange={(event) => setMethod(event.target.value as "HEAD" | "GET")}
              >
                <option value="HEAD">HEAD</option>
                <option value="GET">GET with byte range</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="redirect-max">Max redirects</Label>
              <Input
                id="redirect-max"
                type="number"
                min={0}
                max={20}
                value={maxRedirects}
                onChange={(event) => setMaxRedirects(Number(event.target.value))}
              />
            </div>
          </div>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm leading-6 text-zinc-600">
            Follows public HTTP and HTTPS redirects, blocks private or local destinations, and
            grades redirect count, final status, HTTPS use, temporary redirects, query preservation,
            and timing.
          </div>
          <ErrorMessage message={error} />
          <Button type="button" onClick={checkRedirects} disabled={busy}>
            {busy ? <Network className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {busy ? "Checking..." : "Check redirect chain"}
          </Button>
          {result?.analysis.topFixes.length ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
              <p>Review the priority cleanup items before changing canonical or migration rules.</p>
            </div>
          ) : null}
        </div>
      }
      output={<RedirectChainResult result={result} view={view} />}
    />
  );
}

const securityStatusStyles: Record<SecurityHeaderStatus, string> = {
  pass: "border-emerald-200 bg-emerald-50 text-emerald-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  fail: "border-red-200 bg-red-50 text-red-800",
  info: "border-sky-200 bg-sky-50 text-sky-800",
};

const securityStatusIcons = {
  pass: CheckCircle2,
  warning: AlertTriangle,
  fail: XCircle,
  info: Info,
};

function securityScoreTone(score: number) {
  if (score >= 90) {
    return "text-emerald-700";
  }

  if (score >= 70) {
    return "text-amber-700";
  }

  return "text-red-700";
}

function SecurityFindingCard({ finding }: { finding: SecurityHeaderFinding }) {
  const Icon = securityStatusIcons[finding.status];

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium",
                securityStatusStyles[finding.status],
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {finding.status}
            </span>
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              {finding.category}
            </span>
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              {finding.score}/{finding.maxScore || 0}
            </span>
          </div>
          <h3 className="text-sm font-semibold text-zinc-950">{finding.title}</h3>
          <p className="mt-1 text-sm leading-6 text-zinc-600">{finding.summary}</p>
        </div>
        <p className="shrink-0 rounded-md bg-zinc-50 px-2 py-1 font-mono text-xs text-zinc-500">
          {finding.header}
        </p>
      </div>
      {finding.details?.length ? (
        <ul className="mt-3 space-y-1 text-sm leading-6 text-zinc-600">
          {finding.details.map((detail) => (
            <li key={detail}>- {detail}</li>
          ))}
        </ul>
      ) : null}
      <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm leading-6 text-zinc-700">
        {finding.recommendation}
      </div>
      {finding.evidence ? (
        <CodeOutput value={finding.evidence} className="mt-3 min-h-0 text-xs" />
      ) : null}
    </div>
  );
}

function stringifySecurityHeadersResult(result: SecurityHeadersAuditResponse | null) {
  if (!result) {
    return "";
  }

  return formatSecurityHeaderAuditReport({
    requestedUrl: result.requestedUrl,
    finalUrl: result.finalUrl,
    status: result.status,
    score: result.audit.score,
    grade: result.audit.grade,
    findings: result.audit.findings,
    topFixes: result.audit.topFixes,
  });
}

function SecurityHeadersResult({
  result,
  view,
}: {
  result: SecurityHeadersAuditResponse | null;
  view: "findings" | "headers" | "baseline";
}) {
  if (!result) {
    return <CodeOutput placeholder="Audit grade, findings, and recommended headers will appear here." />;
  }

  const failedOrWarnings = result.audit.findings.filter(
    (finding) => finding.status === "fail" || finding.status === "warning",
  );
  const passedOrInfo = result.audit.findings.filter(
    (finding) => finding.status === "pass" || finding.status === "info",
  );

  if (view === "headers") {
    return (
      <div className="space-y-4">
        {result.redirectChain.length ? (
          <div className="overflow-hidden rounded-md border border-zinc-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr>
                  <th className="px-3 py-2 font-medium">Hop</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Location</th>
                </tr>
              </thead>
              <tbody>
                {result.redirectChain.map((hop, index) => (
                  <tr key={`${hop.url}-${index}`} className="border-t border-zinc-100">
                    <td className="break-all px-3 py-2 font-mono text-zinc-700">{hop.url}</td>
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-zinc-950">
                      {hop.status} {hop.statusText}
                    </td>
                    <td className="break-all px-3 py-2 font-mono text-zinc-700">
                      {hop.location}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="overflow-hidden rounded-md border border-zinc-200">
          <table className="w-full text-left text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="w-56 px-3 py-2 font-medium">Header</th>
                <th className="px-3 py-2 font-medium">Value</th>
              </tr>
            </thead>
            <tbody>
              {result.headers.map((header) => (
                <tr key={header.name} className="border-t border-zinc-100">
                  <td className="break-all px-3 py-2 font-mono text-zinc-700">{header.name}</td>
                  <td className="break-all px-3 py-2 font-mono text-zinc-950">{header.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (view === "baseline") {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-800">
          Use these as a starting point. CSP, COEP, and CORP can break resources when copied without
          adjusting sources for the app.
        </div>
        <div className="space-y-3">
          {result.audit.recommendedHeaders.map((header) => (
            <div key={header.name} className="rounded-md border border-zinc-200 bg-white p-3">
              <h3 className="text-sm font-semibold text-zinc-950">{header.name}</h3>
              <CodeOutput
                value={`${header.name}: ${header.value}`}
                className="mt-2 min-h-0 text-xs"
              />
              <p className="mt-2 text-sm leading-6 text-zinc-600">{header.reason}</p>
            </div>
          ))}
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold text-zinc-700">Nginx</h3>
          <CodeOutput value={result.audit.nginxSnippet} className="min-h-0 text-xs" />
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold text-zinc-700">Next.js</h3>
          <CodeOutput value={result.audit.nextConfigSnippet} className="min-h-0 text-xs" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-[10rem_1fr]">
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-center">
          <p className={cn("text-5xl font-semibold", securityScoreTone(result.audit.score))}>
            {result.audit.grade}
          </p>
          <p className="mt-2 font-mono text-sm text-zinc-600">{result.audit.score}/100</p>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <p className="text-zinc-500">Final URL</p>
              <p className="mt-1 break-all font-mono text-zinc-950">{result.finalUrl}</p>
            </div>
            <div>
              <p className="text-zinc-500">Response</p>
              <p className="mt-1 font-mono text-zinc-950">
                {result.method} {result.status} {result.statusText || (result.ok ? "OK" : "")}
              </p>
            </div>
            <div>
              <p className="text-zinc-500">Time</p>
              <p className="mt-1 font-mono text-zinc-950">{result.durationMs} ms</p>
            </div>
            <div>
              <p className="text-zinc-500">Findings</p>
              <p className="mt-1 font-mono text-zinc-950">
                {result.audit.counts.fail} fail / {result.audit.counts.warning} warning /{" "}
                {result.audit.counts.pass} pass
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm leading-6 text-zinc-600">{result.audit.summary}</p>
        </div>
      </div>

      {result.audit.topFixes.length ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <h3 className="text-sm font-semibold text-red-900">Top fixes</h3>
          <div className="mt-2 space-y-2 text-sm leading-6 text-red-800">
            {result.audit.topFixes.map((finding) => (
              <p key={finding.id}>
                <span className="font-medium">{finding.header}:</span> {finding.recommendation}
              </p>
            ))}
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        {failedOrWarnings.map((finding) => (
          <SecurityFindingCard key={finding.id} finding={finding} />
        ))}
        {passedOrInfo.map((finding) => (
          <SecurityFindingCard key={finding.id} finding={finding} />
        ))}
      </div>
    </div>
  );
}

export function SecurityHeadersAuditorTool() {
  const [url, setUrl] = useState("");
  const [method, setMethod] = useState<"HEAD" | "GET">("HEAD");
  const [result, setResult] = useState<SecurityHeadersAuditResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<"findings" | "headers" | "baseline">("findings");
  const report = useMemo(() => stringifySecurityHeadersResult(result), [result]);
  const baseline = result?.audit.recommendedHeaderBlock ?? "";

  const clear = () => {
    setUrl("");
    setMethod("HEAD");
    setResult(null);
    setError("");
    setView("findings");
  };

  const audit = async () => {
    if (!url.trim()) {
      setError("Enter a URL first.");
      return;
    }

    setBusy(true);
    setError("");
    setResult(null);

    try {
      const response = await fetch("/api/security-headers-auditor", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url, method }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error ?? "Security headers could not be audited.");
      }

      setResult(data as SecurityHeadersAuditResponse);
      setView("findings");
    } catch (auditError) {
      setError(
        auditError instanceof Error ? auditError.message : "Security headers could not be audited.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <InputOutputPanel
      inputTitle="Public URL"
      outputTitle="Security audit"
      inputActions={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setUrl("https://example.com")}
          >
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton onClear={clear} />
        </>
      }
      outputActions={
        <>
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {(["findings", "headers", "baseline"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={view === option ? "default" : "ghost"}
                onClick={() => setView(option)}
              >
                {option === "findings" ? "Findings" : option === "headers" ? "Headers" : "Baseline"}
              </Button>
            ))}
          </div>
          <CopyButton value={report} label="Copy report" />
          <CopyButton value={baseline} label="Copy baseline" />
        </>
      }
      input={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="security-headers-url">URL</Label>
            <Input
              id="security-headers-url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://example.com"
            />
          </div>
          <div className="max-w-xs space-y-2">
            <Label htmlFor="security-headers-method">Method</Label>
            <Select
              id="security-headers-method"
              value={method}
              onChange={(event) => setMethod(event.target.value as "HEAD" | "GET")}
            >
              <option value="HEAD">HEAD</option>
              <option value="GET">GET with byte range</option>
            </Select>
          </div>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm leading-6 text-zinc-600">
            Audits public HTTP response headers only. Private, local, and reserved hosts are
            blocked before the server request is made.
          </div>
          <ErrorMessage message={error} />
          <Button type="button" onClick={audit} disabled={busy}>
            {busy ? <Network className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
            {busy ? "Auditing..." : "Audit security headers"}
          </Button>
        </div>
      }
      output={<SecurityHeadersResult result={result} view={view} />}
    />
  );
}
